import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthUserGuard } from '@bilancio/auth';
import { AuthenticationService, OneFrontAuthGuard } from '@vapor/angular-sdk';
import {
    VAPOR_ACCESS_DENIED_CONFIG,
    VAPOR_LOGIN_ACTION_CONFIG
} from '@vapor/angular-sdk-ui';
import { NgxPermissionsGuard } from 'ngx-permissions';
import { WhitePageComponent } from './components/white-page/white-page.component';
import { OneFrontBilTokenGuard } from './core/guard/one-front-bil-token.guard';
import { OnlyDepositGuard } from './core/guard/only-deposit.guard';
import { loginPathConfig } from './core/tokens/login-path.token';

/** Lista delle rotte da impostare */
const routes = (loginPath: string): Routes => [
    {
        path: '',
        loadChildren: () =>
            import('./main-container/main-container.module').then(
                (m) => m.MainContainerModule
            ),
        canActivateChild: [AuthUserGuard],
        data: {
            layout: {
                showHeader: false,
                showSidebar: false
            }
        }
    },
    {
        path: 'oneFront',
        canActivate: [OneFrontAuthGuard],
        canActivateChild: [OneFrontBilTokenGuard],
        loadChildren: () =>
            import('./main-container/main-container.module').then(
                (m) => m.MainContainerModule
            ),
        data: {
            layout: {
                showHeader: false,
                showSidebar: false
            }
        }
    },

    {
        path: 'db25aa4d9af14499d2e469708f85495671101656e7405508ac56971d9d6636be',
        loadChildren: () =>
            import('./login/login.module').then((m) => m.LoginModule),
        data: {
            layout: {
                showHeader: false,
                showSidebar: false
            }
        }
    },
    {
        path: 'loginIntegration',
        loadChildren: () =>
            import('./login-integration/login-integration.module').then(
                (m) => m.LoginIntegrationModule
            ),
        data: {
            layout: {
                showHeader: false,
                showSidebar: false
            }
        }
    },
    {
        path: 'lyberaIntegration',
        loadChildren: () =>
            import('./lybera-integration/lybera-integration.module').then(
                (m) => m.LyberaIntegrationModule
            ),
        data: {
            layout: {
                showHeader: false,
                showSidebar: false
            }
        }
    },
    {
        path: 'depositoViaLibera',
        loadChildren: () =>
            import('./vialibera-deposit/vialibera-deposit.module').then(
                (m) => m.VialiberaDepositModule
            ),
        data: {
            layout: {
                showHeader: false,
                showSidebar: false
            }
        }
    },
    {
        path: 'deposito',
        component: WhitePageComponent,
        canActivate: [AuthUserGuard],
        data: {
            layout: {
                showHeader: true,
                showSidebar: true
            }
        }
    },
    {
        path: 'manage',
        loadChildren: () =>
            import('./manage/manage.module').then((m) => m.ManageModule),
        canActivate: [AuthUserGuard, OnlyDepositGuard, NgxPermissionsGuard],
        data: {
            layout: {
                showHeader: true,
                showSidebar: true
            },
            permissions: {
                only: 'ADMIN',
                redirectTo: '/user/profile'
            }
        }
    },
    {
        path: 'wizard',
        loadChildren: () =>
            import('./wizard/wizard.module').then((m) => m.WizardModule),
        canActivate: [AuthUserGuard, OnlyDepositGuard],
        data: {
            layout: {
                showHeader: true,
                showSidebar: true
            }
        }
    },
    {
        // Define user paths
        path: 'user',
        canActivate: [AuthUserGuard, OnlyDepositGuard],
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'dashboard'
            },
            {
                // Show loggedin user dashboard
                path: 'dashboard',
                loadChildren: () =>
                    import('./user-dashboard/user-dashboard.module').then(
                        (m) => m.UserDashboardModule
                    )
            },
            {
                // Show loggedin user profile
                path: 'profile',
                loadChildren: () =>
                    import('./user-profile/user-profile.module').then(
                        (m) => m.UserProfileModule
                    )
            }
        ],
        data: {
            layout: {
                showHeader: true,
                showSidebar: true
            }
        }
    },
    {
        path: 'white',
        component: WhitePageComponent,
        data: {
            layout: {
                showHeader: true,
                showSidebar: true
            }
        }
    },
    {
        path: 'errorPage',
        loadChildren: () =>
            import('./components/error-page/error-page.module').then(
                (m) => m.ErrorPageModule
            ),
        data: {
            layout: {
                showHeader: false,
                showSidebar: false
            }
        }
    },
    {
        path: 'notFound',
        loadChildren: () =>
            import('./components/page-not-found/page-not-found.module').then(
                (m) => m.PageNotFoundModule
            ),
        data: {
            layout: {
                showHeader: false,
                showSidebar: false
            }
        }
    },
    {
        path: loginPath,
        loadComponent: () =>
            import('@vapor/angular-sdk-ui/pages').then(
                (p) => p.VaporLoginRequiredComponent
            ),
        providers: [
            {
                provide: VAPOR_LOGIN_ACTION_CONFIG,
                useFactory: (service: AuthenticationService) => ({
                    login: () => service.login()
                }),
                deps: [AuthenticationService]
            }
        ]
    },
    {
        path: 'access-denied',
        loadComponent: () =>
            import('@vapor/angular-sdk-ui/pages').then(
                (p) => p.VaporAccessDeniedComponent
            ),
        providers: [
            {
                provide: VAPOR_ACCESS_DENIED_CONFIG,
                useFactory: () => ({
                    fn: () => (window.location.href = `/#/${loginPath}`)
                })
            }
        ]
    },
    {
        path: '**',
        redirectTo: '/notFound'
    }
];

/**
 * Modulo con le rotte principali del programma
 *
 * @export
 * @class AppRoutingModule
 */
@NgModule({
    declarations: [],
    imports: [
        RouterModule.forRoot(routes(loginPathConfig.loginPath), {
            useHash: true,
            onSameUrlNavigation: 'reload',
            enableTracing: false
        })
    ],
    exports: [RouterModule]
})
export class AppRoutingModule {}
